import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useEffect, useState } from 'react';
import { CboRole } from '@cbo/shared-library';
import { GridRowId } from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import {
  getAllUpcomingRuleChangesetIds,
  getCurrentDateString,
  getJurisdictionRules,
  getSplitShiftRuleDescription,
  isTippedMinWageRule,
  organizeRuleConfigsByScope,
  getDefaultConfigsByRuleTypeAndJurisdictionPath,
  buildSchoolDayDefRuleConfig,
  getRulesForTipMakeupPayUpdate,
} from '../utils/utils';
import LaborRuleTemplate from '../LaborRuleTemplate/LaborRuleTemplate';
import {
  LaborRuleConfigData,
  LaborRule,
  LaborRuleGroup,
  RuleChangeReason,
  RuleFormValues,
  SchedulingRuleTypes,
  WagesRuleTypes,
  MinorRuleTypes,
  BreakRuleTypes,
  LaborRuleApplicabilityData,
  ModalType,
  TipMakeupPayCalculationModalConfig,
} from '../models/LaborRuleConfiguration';
import JurisdictionSitesDataGrid, {
  JurisdictionSitesRow,
} from '../JurisdictionSitesDataGrid/JurisdictionSitesDataGrid';
import routes from '../../constants/routes';
import { RuleWithEffectiveDate } from '../RuleWithEffectiveDate/RuleWithEffectiveDate';
import DeactivateModal from '../modals/DeactivateModal/DeactivateModal';
import { ConfirmationFormValues } from '../modals/LaborRuleBaseConfirmationModal/LaborRuleBaseConfirmationModal';
import { useDeleteUpcomingRuleConfigurations, useSaveNewRuleConfiguration } from '../requests/mutations';
import { handleSaveAndDelete } from '../LaborRuleEditView/utils/formSubmitHandlers';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useUsers } from '../../contexts/userContext';
import isPermitted from '../../lib/permissions';
import TipCreditCalculationModal from '../modals/TippedMinWageModal/TipCreditCalculationModal';
import laborRulesKeys from '../requests/queryKeys';

interface LaborRuleCardProps {
  title: string;
  ruleType: string;
  ruleGroup: LaborRuleGroup;
  ruleConfigs: LaborRuleConfigData[];
  sites: JurisdictionSitesRow[] | undefined;
  jurisdictionPath: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchConfigs: any;
  orgRuleConfigsLoading: boolean;
  defaultRuleConfigs: LaborRuleConfigData[];
  selectedJurisdiction: string | undefined;
  ruleApplicabilities: LaborRuleApplicabilityData[] | undefined;
  furthestAwayWorkWeekDate: string;
}

const PROHIBITED_FROM_DEACTIVATION = [
  WagesRuleTypes.MinimumWageRule as string,
  WagesRuleTypes.TipMakeupPayRule as string,
];

function getEditLaborRuleButton(
  ruleType: string,
  isAdminRole: boolean,
  isUserLREWriter: boolean,
  checked: boolean,
  onEditRule: () => void,
  t: (key: string) => string
) {
  if (ruleType === WagesRuleTypes.MinimumWageRule) {
    return (
      <Button
        disabled={isUserLREWriter}
        onClick={() => onEditRule()}
        variant='contained'
        data-testid='edit-rule-button'
      >
        {t('laborRules.editLaborRuleText')}
      </Button>
    );
  }
  if (ruleType === WagesRuleTypes.TipMakeupPayRule) {
    return (
      <Button
        endIcon={<OpenInNewIcon />}
        onClick={() => {
          window.open(`${routes.JOBCODE_CONFIGURATION_PAGE}`, '_blank');
        }}
        variant='contained'
        data-testid='view-job-button'
      >
        {isAdminRole ? t('laborRules.editLaborRuleViewJobsText') : t('laborRules.viewJobConfigurationText')}
      </Button>
    );
  }
  return (
    <Button
      disabled={!checked || isUserLREWriter}
      onClick={() => onEditRule()}
      variant='contained'
      data-testid='edit-rule-button'
    >
      {t('laborRules.editLaborRuleText')}
    </Button>
  );
}

function LaborRuleCard(props: Readonly<LaborRuleCardProps>) {
  const { t } = useTranslation();
  const {
    title,
    ruleConfigs,
    sites,
    jurisdictionPath,
    ruleGroup,
    refetchConfigs,
    orgRuleConfigsLoading,
    defaultRuleConfigs,
    selectedJurisdiction,
    ruleApplicabilities,
    furthestAwayWorkWeekDate,
  } = props;
  const { ruleType } = props;
  const history = useHistory();
  const onEditRule = (stateOverrides?: Record<string, unknown>) => {
    history.push({
      pathname: `/settings/labor/labor-rules/${ruleType}/edit`,
      state: { lastLocation: routes.LABOR_RULES, ...stateOverrides },
    });
  };

  const jurisdictionNormal = jurisdictionPath.split('.').at(1)?.replaceAll('_', ' ') ?? jurisdictionPath;
  const [checked, setChecked] = useState(true);
  const queryClient = useQueryClient();
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const saveNewRuleConfigurationMutation = useSaveNewRuleConfiguration();
  const deleteRuleConfigurationsMutation = useDeleteUpcomingRuleConfigurations();
  const { setSnackbarState } = useSnackbar();
  const theme = useTheme();
  const user = useUsers();
  const isAdminRole = isPermitted(user, [CboRole.LABOR_CONFIG_ADMIN]);
  const isUserLREWriter = !isPermitted(user, [CboRole.LRE_RULE_WRITER]);

  const [calculationModal, setCalculationModal] = useState<TipMakeupPayCalculationModalConfig>({
    open: false,
    mode: null,
    selectedRows: [],
  });
  const determineIfHasActiveConfigs = (_ruleType: string, activeJurisdictionRules: LaborRuleConfigData[]) => {
    if (!activeJurisdictionRules || activeJurisdictionRules.length === 0) return false;
    if (_ruleType === WagesRuleTypes.MinimumWageRule && isTippedMinWageRule(activeJurisdictionRules[0].rule)) {
      return activeJurisdictionRules.length > 1;
    }
    return activeJurisdictionRules.length > 0;
  };

  // If there are any rules that are split into two subtypes, this gets the subtype, while 'ruleType' is the true rule type
  const parsedRuleType = ruleType.split('.')[0];

  let laborRuleTemplates;
  // Group by scope and get jurisdiction only rules
  const jurisdictionRules = getJurisdictionRules(organizeRuleConfigsByScope(ruleConfigs), jurisdictionPath);

  // Get active jurisdiction rules
  const currentJurisdictionRules = jurisdictionRules.filter((x) => x.effectiveDate <= getCurrentDateString());
  const effectiveUntilDate = jurisdictionRules
    .slice()
    .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate))
    .filter((x) => x.effectiveDate > getCurrentDateString())?.[0]?.effectiveDate;
  const currentDefaultRuleConfigs = getDefaultConfigsByRuleTypeAndJurisdictionPath(
    defaultRuleConfigs,
    ruleType,
    jurisdictionPath
  );

  const hasConfigs = determineIfHasActiveConfigs(ruleType, jurisdictionRules);
  useEffect(() => {
    setChecked(hasConfigs);
  }, [hasConfigs, ruleType]);

  const hasSummerVacationRule = jurisdictionRules.some(
    (ruleConfig) => ruleConfig?.rule.type === MinorRuleTypes.SummerVacationRule
  );

  const hasMinorSchoolDaysRule = jurisdictionRules.some(
    (ruleConfig) => ruleConfig?.rule.type === MinorRuleTypes.MinorSchoolDaysRule
  );

  if (
    currentJurisdictionRules.length > 1 &&
    parsedRuleType === MinorRuleTypes.MinorSchoolDayDefinitionRule &&
    hasSummerVacationRule &&
    hasMinorSchoolDaysRule
  ) {
    const schoolDayDefConfig = buildSchoolDayDefRuleConfig(jurisdictionRules);
    laborRuleTemplates = (
      <RuleWithEffectiveDate
        effectiveDate={schoolDayDefConfig.effectiveDate}
        effectiveUntil={effectiveUntilDate}
        currentConfigs={[schoolDayDefConfig]}
        ruleType={schoolDayDefConfig.rule.type}
        mainView
        currentDefaultRuleConfigs={currentDefaultRuleConfigs}
      />
    );
  } else if (hasConfigs && currentJurisdictionRules.length > 0) {
    const { effectiveDate, rule } = currentJurisdictionRules[0];
    laborRuleTemplates = (
      <RuleWithEffectiveDate
        effectiveDate={effectiveDate}
        effectiveUntil={effectiveUntilDate}
        currentConfigs={currentJurisdictionRules}
        ruleType={rule.type}
        mainView
        currentDefaultRuleConfigs={currentDefaultRuleConfigs}
        locality={selectedJurisdiction ?? jurisdictionPath}
      />
    );
  } else {
    laborRuleTemplates = (
      <LaborRuleTemplate
        key='noGuidance'
        id={0}
        data-testid='labor-rule-template-noGuidance'
        sx={{
          mt: 2,
        }}
        rule={{} as LaborRule}
        isMealBreakVermontJurisdiction={
          (selectedJurisdiction ?? jurisdictionPath)?.includes('Vermont') && ruleType === BreakRuleTypes.MealBreakRule
        }
      />
    );
  }

  const ruleFormWithNoRules = (_jurisdictionPath: string): RuleFormValues => ({
    hierarchyPath: _jurisdictionPath,
    ruleType,
    rules: [],
    effectiveDate: getCurrentDateString(),
    changeReason: RuleChangeReason.COMPANY_POLICY,
    defaultRuleId: null,
  });

  const deactivateAction = (confirmData: ConfirmationFormValues) =>
    handleSaveAndDelete({
      data: { ...ruleFormWithNoRules(jurisdictionPath) },
      formData: confirmData,
      changesetsToDelete: getAllUpcomingRuleChangesetIds(ruleConfigs),
      handleSuccessProps: {
        ruleName: t(`laborRules.rules.${ruleType}.name`),
        effectiveDate: confirmData.effectiveDate,
        jurisdictionName: jurisdictionNormal,
        push: history.push,
        setOpenModal: setDeactivateModalOpen,
        refetchConfigs,
        setSnackbarState,
        t,
      },
      saveNewRuleConfigurationMutation,
      deleteRuleConfigurationsMutation,
    });

  const handleCalculationClick = (mode: ModalType, selectedRows: GridRowId[]) => {
    setCalculationModal({ open: true, mode, selectedRows });
  };

  const handleCalculationModalAction = async (confirmData: ConfirmationFormValues) => {
    const handleSuccessProps = {
      ruleName: t(`laborRules.rules.${ruleType}.name`),
      effectiveDate: confirmData.effectiveDate,
      jurisdictionName: jurisdictionNormal,
      push: history.push,
      setOpenModal: setDeactivateModalOpen,
      refetchConfigs,
      setSnackbarState,
      t,
    };
    const selectedSiteIds = calculationModal.selectedRows.map(String);

    const ruleFormToTurnonTipMakeupPay = {
      hierarchyPath: jurisdictionPath,
      ruleType,
      rules: getRulesForTipMakeupPayUpdate(selectedSiteIds, jurisdictionRules),
      effectiveDate: confirmData.effectiveDate,
      changeReason: confirmData.changeReason || RuleChangeReason.COMPANY_POLICY,
      defaultRuleId: null,
      siteIds: selectedSiteIds,
    };

    const requestBody = {
      data:
        calculationModal.mode === ModalType.TurnOnCalculation
          ? ruleFormToTurnonTipMakeupPay
          : { ...ruleFormWithNoRules(jurisdictionPath), siteIds: selectedSiteIds },
      changesetsToDelete:
        calculationModal.mode === ModalType.TurnOnCalculation ? [] : getAllUpcomingRuleChangesetIds(ruleConfigs),
      formData: confirmData,
      handleSuccessProps,
      saveNewRuleConfigurationMutation,
      deleteRuleConfigurationsMutation,
    };

    await handleSaveAndDelete(requestBody);
    queryClient.invalidateQueries({
      queryKey: laborRulesKeys.ruleApplicability.listByJurisdiction(jurisdictionPath),
    });
    setCalculationModal({ open: false, mode: null, selectedRows: [] });
  };

  const handleCalculationModalClose = () => {
    setCalculationModal({ open: false, mode: null, selectedRows: [] });
  };

  return (
    <Box
      data-testid={`laborRuleCard-${title}`}
      sx={{ background: theme.palette.background.paper, padding: '1.5rem', marginTop: 2, borderRadius: 1 }}
    >
      {deactivateModalOpen && (
        <DeactivateModal
          ruleName={t(`laborRules.rules.${ruleType}.name`)}
          jurisdiction={jurisdictionPath.split('.')[1]}
          loading={
            saveNewRuleConfigurationMutation.isLoading ||
            deleteRuleConfigurationsMutation.isLoading ||
            orgRuleConfigsLoading
          }
          handleClose={() => {
            setChecked(true);
            setDeactivateModalOpen(false);
          }}
          handleDeactivate={deactivateAction}
        />
      )}
      {calculationModal.open && calculationModal.mode && (
        <TipCreditCalculationModal
          mode={calculationModal.mode}
          handleClose={handleCalculationModalClose}
          handleAction={handleCalculationModalAction}
          loading={false}
          furthestAwayWorkWeekDate={furthestAwayWorkWeekDate}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          {!PROHIBITED_FROM_DEACTIVATION.includes(ruleType) && (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isUserLREWriter}
                  data-testid={`${ruleType}-deactivate-checkbox`}
                  aria-label={t(`laborRules.dropdownAriaLabels.${checked ? 'ruleActivated' : 'ruleDeactivated'}`) ?? ''}
                  checked={checked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setChecked(e.target.checked);
                    if (hasConfigs) setDeactivateModalOpen(!e.target.checked);
                  }}
                  sx={{ pr: 0 }}
                />
              }
              label=''
            />
          )}
          <Typography variant='h6'>{title}</Typography>
        </Box>
        {getEditLaborRuleButton(ruleType, isAdminRole, isUserLREWriter, checked, onEditRule, t)}
      </Box>
      <Box sx={{ mt: '2rem' }}>
        {laborRuleTemplates}
        {ruleType === SchedulingRuleTypes.SplitShiftRule && <Box mt={3}>{getSplitShiftRuleDescription(t)}</Box>}
        {sites && (
          <Box data-testid='jurisdiction-sites-table-container'>
            <Box sx={{ my: '1.5rem' }}>
              <Typography data-testid='jurisdiction-sites-table-header' variant='subtitle1'>
                {t(`laborRules.sitesHeader`)}
              </Typography>
            </Box>
            <JurisdictionSitesDataGrid
              sites={sites}
              jurisdictionPath={jurisdictionPath}
              ruleGroup={ruleGroup}
              onEditRule={onEditRule}
              allRules={ruleConfigs}
              ruleType={ruleType}
              onCalculationClick={handleCalculationClick}
              ruleApplicabilities={ruleApplicabilities}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default LaborRuleCard;
